/*
 * <si-video> — SimplyInked-owned lazy video element.
 * Replaces the former third-party <spectrum-video> element (same data-* API).
 *
 * Attributes:
 *   data-src            MP4 URL
 *   data-sources        JSON [{url, width}] — best width is picked for the element size
 *   data-poster         Poster URL (an <img> child is shown until playback)
 *   data-autoplay       Muted autoplay while visible
 *   data-loop           Loop playback
 *   data-load           viewport (default) | hover | click | eager
 *   data-preload-margin IntersectionObserver rootMargin for loading (default 200px)
 *   data-visibility-threshold  0..1, share visible before autoplay plays (default 0.5)
 *
 * Behaviour:
 *   - Video source is only fetched when the element nears the viewport.
 *   - Autoplay pauses when the element leaves the viewport.
 *   - At most SIVideo.maxConcurrent (default 2) autoplaying videos play at once;
 *     starting another pauses the oldest.
 *   - prefers-reduced-motion: no autoplay; the poster stays and a tap plays it.
 *   - Tap/click toggles playback (manual-play fallback when autoplay is blocked).
 *   - Always muted + playsinline so phones play inline.
 *
 * Events (bubbling): si-video:loaded, si-video:play, si-video:pause,
 *   si-video:error, si-video:unloaded, si-video:mute, si-video:unmute
 */
(function () {
  if (typeof customElements === 'undefined' || customElements.get('si-video')) return;

  var reduceMotion = false;
  try {
    reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) {}

  var activeUnmuted = null;
  var playing = []; // autoplaying elements currently playing, oldest first

  function notePlaying(el) {
    playing = playing.filter(function (x) { return x !== el; });
    playing.push(el);
    var max = SIVideo.maxConcurrent;
    while (playing.length > max) {
      var oldest = playing.shift();
      if (oldest && oldest !== el) oldest._autoPause();
    }
  }

  function noteStopped(el) {
    playing = playing.filter(function (x) { return x !== el; });
  }

  class SIVideo extends HTMLElement {
    static get observedAttributes() { return ['data-src', 'data-sources']; }

    connectedCallback() {
      this._state = 'idle';
      this._video = null;
      this._loadObserver = null;
      this._visibilityObserver = null;
      this._wantsPlay = false;
      this._visible = false;
      if (!this.style.display) this.style.display = 'block';
      if (!this.style.position) this.style.position = 'relative';
      this.style.overflow = 'hidden';
      this._tapHandler = this._onTap.bind(this);
      this.addEventListener('click', this._tapHandler);
      this._setupLoad();
    }

    disconnectedCallback() {
      this._teardown();
      if (activeUnmuted === this) activeUnmuted = null;
      noteStopped(this);
    }

    attributeChangedCallback(name, oldVal, newVal) {
      if (oldVal === newVal) return;
      if (this._state !== 'idle') { this.unload(); this._setupLoad(); }
    }

    // Public API (kept compatible with the previous element)
    play() {
      this._wantsPlay = true;
      if (!this._video) { this._loadVideo(); return; }
      if (this._state === 'ready') this._safePlay();
    }
    pause() { if (this._video) this._video.pause(); noteStopped(this); }
    load() { if (!this._video) this._loadVideo(); }
    unload() {
      if (this._state === 'idle' && !this._video) return;
      if (this._video) {
        this._video.pause();
        this._video.removeAttribute('src');
        this._video.load();
        this._video.remove();
        this._video = null;
      }
      noteStopped(this);
      this._showPoster();
      this._wantsPlay = false;
      this._state = 'idle';
      this._dispatch('unloaded');
    }
    mute() {
      if (this._video) this._video.muted = true;
      if (activeUnmuted === this) activeUnmuted = null;
      this._dispatch('mute');
    }
    unmute() {
      if (activeUnmuted && activeUnmuted !== this) activeUnmuted.mute();
      activeUnmuted = this;
      if (this._video) this._video.muted = false;
      this._dispatch('unmute');
    }
    get playing() { return this._state === 'ready' && !!this._video && !this._video.paused; }
    get loaded() { return this._state === 'ready'; }
    get muted() { return this._video ? this._video.muted : true; }

    // Private
    _autoplayAllowed() { return !!this.dataset.autoplay && !reduceMotion; }

    _setupLoad() {
      var mode = this.dataset.load || 'viewport';
      if (mode === 'eager') { this._loadVideo(); return; }
      if (mode === 'hover') {
        this._hoverHandler = () => { this._loadVideo(); this.removeEventListener('pointerenter', this._hoverHandler); };
        this.addEventListener('pointerenter', this._hoverHandler);
        return;
      }
      if (mode === 'click') return; // loaded by _onTap
      if (reduceMotion && this.dataset.autoplay) return; // poster only until tapped
      var margin = this.dataset.preloadMargin || '200px';
      if (typeof IntersectionObserver === 'undefined') { this._loadVideo(); return; }
      var observer = new IntersectionObserver((entries) => {
        if (entries[0] && entries[0].isIntersecting) {
          this._loadVideo();
          observer.disconnect();
          if (this._loadObserver === observer) this._loadObserver = null;
        }
      }, { rootMargin: margin });
      this._loadObserver = observer;
      observer.observe(this);
    }

    _onTap(e) {
      // Tap-to-play only applies when playback can't start on its own:
      // click-to-load mode, reduced motion, or autoplay blocked by the browser.
      // Otherwise clicks fall through to the host card (e.g. opening a dialog).
      var manual = this.dataset.load === 'click' || this.hasAttribute('data-needs-tap') ||
        (reduceMotion && this.dataset.autoplay);
      if (!manual) return;
      // Let links/buttons inside the element (product tags, CTAs) work normally.
      if (e && e.target && e.target.closest && e.target.closest('a,button') && e.target.closest('a,button') !== this) return;
      if (!this._video) { this._wantsPlay = true; this._loadVideo(); return; }
      if (this._video.paused) this._safePlay(); else this.pause();
    }

    _safePlay() {
      if (!this._video) return;
      var p = this._video.play();
      if (p && typeof p.then === 'function') {
        p.then(() => { notePlaying(this); this._dispatch('play'); })
         .catch(() => { this._showPoster(); this.setAttribute('data-needs-tap', ''); });
      } else {
        notePlaying(this);
      }
    }

    _autoPause() {
      if (this._video && !this._video.paused) { this._video.pause(); this._dispatch('pause'); }
    }

    _loadVideo() {
      if (this._state !== 'idle') return;
      this._state = 'loading';
      var video = document.createElement('video');
      video.muted = true;
      video.setAttribute('muted', '');
      video.loop = this.hasAttribute('data-loop');
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.setAttribute('disablepictureinpicture', '');
      video.preload = 'metadata';
      if (this.dataset.poster) video.poster = this.dataset.poster;
      Object.assign(video.style, { position: 'absolute', inset: '0', width: '100%', height: '100%', objectFit: 'cover' });
      video.addEventListener('loadedmetadata', () => this._onCanPlay(), { once: true });
      video.addEventListener('error', () => this._onError(), { once: true });
      this._video = video;
      this.appendChild(video);
      video.src = this._pickSource();
      video.load();
    }

    _pickSource() {
      var attr = this.dataset.sources;
      if (attr) {
        try {
          var sources = JSON.parse(attr);
          if (Array.isArray(sources) && sources.length) {
            var target = this.clientWidth * (window.devicePixelRatio || 1);
            var sorted = sources.slice().sort(function (a, b) { return a.width - b.width; });
            var match = sorted.find(function (s) { return s.width >= target; });
            return (match || sorted[sorted.length - 1]).url;
          }
        } catch (e) {}
      }
      return this.dataset.src || '';
    }

    _onCanPlay() {
      this._state = 'ready';
      this._hidePoster();
      this._dispatch('loaded', { src: this._video.src });
      if (this._autoplayAllowed()) this._setupVisibility();
      else if (this._wantsPlay) this._safePlay();
      this._wantsPlay = false;
    }

    _onError() {
      var msg = (this._video && this._video.error && this._video.error.message) || 'Video load failed';
      var code = this._video && this._video.error && this._video.error.code;
      this._state = 'idle';
      if (this._video) { this._video.remove(); this._video = null; }
      noteStopped(this);
      this._showPoster();
      this._dispatch('error', { error: msg, code: code });
    }

    _setupVisibility() {
      var raw = this.dataset.visibilityThreshold;
      var parsed = raw == null ? 0.5 : parseFloat(raw);
      var threshold = isFinite(parsed) && parsed >= 0 && parsed <= 1 ? parsed : 0.5;
      this._visibilityObserver = new IntersectionObserver((entries) => {
        var entry = entries[0];
        this._visible = !!(entry && entry.isIntersecting);
        if (this._visible) this._safePlay();
        else { this._autoPause(); noteStopped(this); }
      }, { threshold: threshold });
      this._visibilityObserver.observe(this);
    }

    _hidePoster() { var img = this.querySelector(':scope > img'); if (img) img.style.display = 'none'; this.removeAttribute('data-needs-tap'); }
    _showPoster() { var img = this.querySelector(':scope > img'); if (img) img.style.display = ''; }

    _teardown() {
      if (this._loadObserver) { this._loadObserver.disconnect(); this._loadObserver = null; }
      if (this._visibilityObserver) { this._visibilityObserver.disconnect(); this._visibilityObserver = null; }
      if (this._hoverHandler) { this.removeEventListener('pointerenter', this._hoverHandler); this._hoverHandler = null; }
      if (this._tapHandler) { this.removeEventListener('click', this._tapHandler); }
      if (this._video) { this._video.pause(); this._video = null; }
    }

    _dispatch(name, detail) {
      this.dispatchEvent(new CustomEvent('si-video:' + name, { bubbles: true, detail: detail || {} }));
    }
  }

  SIVideo.maxConcurrent = 2;
  window.SIVideo = SIVideo;
  customElements.define('si-video', SIVideo);
  // Release 1 compatibility: existing sections still emit the former vendor's
  // tag name. Same behaviour, owned code. Remove in Release 2.
  if (!customElements.get('spectrum-video')) {
    customElements.define('spectrum-video', class extends SIVideo {});
  }
})();
